import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useData } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fmtPhone } from "../lib/time";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Loader2, Trash2, Edit, Save, Users, Shield, UserPlus, Copy, Clock, ChevronDown, ChevronRight, Eye, Pencil, Check, X, RefreshCw, AlertTriangle } from "lucide-react";
import PushNotificationCard from "../components/PushNotificationCard";
import NotificationRecipientsCard from "../components/NotificationRecipientsCard";
import SmsTemplatesCard from "../components/SmsTemplatesCard";

export default function Settings() {
  const { 
    pipelineStages, fetchPipelineStages, createPipelineStage, updatePipelineStage, deletePipelineStage,
    pipelineCategories, fetchPipelineCategories, createPipelineCategory, updatePipelineCategory, deletePipelineCategory,
    dropdownOptions, fetchDropdownOptions, createDropdownOption, updateDropdownOption, deleteDropdownOption,
  } = useData();
  
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.defaultTab || "company");
  const [companySettings, setCompanySettings] = useState({ company_name: "", company_phone: "", company_email: "", company_address: "" });
  const [savingCompany, setSavingCompany] = useState(false);
  const [profileForm, setProfileForm] = useState({ name: "", phone: "", email: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [showStageModal, setShowStageModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("service_types");
  const [selectedPipelineCategory, setSelectedPipelineCategory] = useState(null);
  const [stageForm, setStageForm] = useState({ name: "", value: "", color: "#0F4C81", order: 0, category: "" });
  const [categoryForm, setCategoryForm] = useState({ name: "", value: "", color: "#0F4C81", order: 0 });
  const [optionForm, setOptionForm] = useState({ category: "service_types", value: "", label: "", color: "", order: 0, enabled: true });
  const [submitting, setSubmitting] = useState(false);
  const [teamUsers, setTeamUsers] = useState([]);
  const [roles, setRoles] = useState({});
  const [allPermissions, setAllPermissions] = useState({});
  const [invites, setInvites] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "sales_rep", name: "", permissions: [] });
  const [inviteLink, setInviteLink] = useState("");
  const [editingMember, setEditingMember] = useState(null);
  const [editMemberPerms, setEditMemberPerms] = useState([]);
  const [editMemberRole, setEditMemberRole] = useState("");
  const { isAdmin, user } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

  useEffect(() => {
    fetchPipelineStages();
    fetchPipelineCategories();
    fetchDropdownOptions();
    if (isAdmin) loadTeam();
  }, [fetchPipelineStages, fetchPipelineCategories, fetchDropdownOptions, isAdmin]);

  const loadTeam = async () => {
    try {
      const [usersRes, rolesRes, invitesRes, permsRes] = await Promise.all([
        axios.get(`${API_URL}/auth/users`),
        axios.get(`${API_URL}/auth/roles`),
        axios.get(`${API_URL}/auth/invites`),
        axios.get(`${API_URL}/auth/permissions`),
      ]);
      setTeamUsers(usersRes.data);
      setRoles(rolesRes.data);
      setInvites(invitesRes.data);
      setAllPermissions(permsRes.data);
    } catch (e) { console.error(e); }
  };

  const loadCompanySettings = async () => {
    try {
      const res = await axios.get(`${API_URL}/quote-settings`);
      setCompanySettings({
        company_name: res.data.company_name || "",
        company_phone: res.data.company_phone || "",
        company_email: res.data.company_email || "",
        company_address: res.data.company_address || "",
      });
    } catch { /* no settings yet */ }
  };

  const saveCompanySettings = async () => {
    setSavingCompany(true);
    try {
      await axios.put(`${API_URL}/quote-settings`, companySettings);
      toast.success("Company info updated!");
    } catch { toast.error("Failed to save"); }
    setSavingCompany(false);
  };

  useEffect(() => { loadCompanySettings(); }, []);

  // Load my own profile so the "My Profile" tab is prefilled with my
  // current name / phone / email.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`);
        setProfileForm({ name: data.name || "", phone: data.phone || "", email: data.email || "" });
      } catch { /* ignored — user may not have a profile yet */ }
    })();
  }, []);

  // When role changes in invite form, load that role's default permissions
  const handleInviteRoleChange = (newRole) => {
    const rolePerms = roles[newRole]?.permissions || [];
    setInviteForm(f => ({ ...f, role: newRole, permissions: [...rolePerms] }));
  };

  const toggleInvitePermission = (permKey) => {
    setInviteForm(f => {
      const has = f.permissions.includes(permKey);
      return { ...f, permissions: has ? f.permissions.filter(p => p !== permKey) : [...f.permissions, permKey] };
    });
  };

  const handleCreateInvite = async () => {
    if (!inviteForm.email) { toast.error("Email is required"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/auth/invites`, inviteForm);
      const fullUrl = `${window.location.origin}/join/${res.data.token}`;
      setInviteLink(fullUrl);
      setInvites(prev => [res.data, ...prev]);
      toast.success("Invite created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invite");
    }
    setSubmitting(false);
  };

  const handleDeleteInvite = async (inviteId) => {
    try {
      await axios.delete(`${API_URL}/auth/invites/${inviteId}`);
      setInvites(prev => prev.filter(i => i.id !== inviteId));
      toast.success("Invite deleted");
    } catch { toast.error("Failed to delete invite"); }
  };

  const handleDeleteMember = async (member) => {
    const label = member.name || member.email || "this user";
    if (!window.confirm(`Permanently remove ${label}?\n\nThey'll lose access immediately. Their historical activity (quotes sent, notes, etc.) stays intact.`)) return;
    try {
      await axios.delete(`${API_URL}/auth/users/${member.id}`);
      setTeamUsers(prev => prev.filter(u => u.id !== member.id));
      toast.success(`${label} removed`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove team member");
    }
  };

  const copyInviteLink = (token) => {
    const url = `${window.location.origin}/join/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied!");
  };

  // Open member permission editor
  const openEditMember = (member) => {
    setEditingMember(member);
    setEditMemberRole(member.role || "sales_rep");
    setEditMemberPerms([...(member.permissions || [])]);
  };

  const toggleMemberPermission = (permKey) => {
    setEditMemberPerms(prev => prev.includes(permKey) ? prev.filter(p => p !== permKey) : [...prev, permKey]);
  };

  const handleMemberRoleChange = (newRole) => {
    const rolePerms = roles[newRole]?.permissions || [];
    setEditMemberRole(newRole);
    setEditMemberPerms([...rolePerms]);
  };

  const saveMemberPermissions = async () => {
    if (!editingMember) return;
    setSubmitting(true);
    try {
      const res = await axios.put(`${API_URL}/auth/users/${editingMember.id}/role`, {
        role: editMemberRole,
        permissions: editMemberPerms
      });
      setTeamUsers(prev => prev.map(u => u.id === editingMember.id ? res.data : u));
      setEditingMember(null);
      toast.success("Permissions updated!");
    } catch (e) { toast.error("Failed to update permissions"); }
    setSubmitting(false);
  };

  const categories = [
    { key: "service_types", label: "Service Types" },
    { key: "lead_sources", label: "Lead Sources" },
    { key: "classifications", label: "Classifications" },
    { key: "supplier_categories", label: "Supplier Categories" },
    { key: "tags", label: "Tags" },
  ];

  // Pipeline Stage handlers
  const handleStageSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const cat = selectedPipelineCategory || stageForm.category;
      const data = { ...stageForm, category: cat, value: stageForm.value || `${cat}_${stageForm.name.toLowerCase().replace(/\s+/g, '_')}` };
      if (editingStage) {
        await updatePipelineStage(editingStage.id, data);
        toast.success("Stage updated!");
      } else {
        await createPipelineStage(data);
        toast.success("Stage created!");
      }
      closeStageModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save stage");
    }
    setSubmitting(false);
  };

  const handleEditStage = (stage) => { setEditingStage(stage); setStageForm(stage); setShowStageModal(true); };
  const handleDeleteStage = async (id) => {
    if (window.confirm("Are you sure? This may affect existing projects.")) {
      try { await deletePipelineStage(id); toast.success("Stage deleted"); }
      catch { toast.error("Failed to delete stage"); }
    }
  };
  const closeStageModal = () => { setShowStageModal(false); setEditingStage(null); setStageForm({ name: "", value: "", color: "#0F4C81", order: 0, category: selectedPipelineCategory || "" }); };

  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = { ...categoryForm, value: categoryForm.value || categoryForm.name.toLowerCase().replace(/\s+/g, '_') };
      if (editingCategory) { await updatePipelineCategory(editingCategory.id, data); toast.success("Category updated!"); }
      else { await createPipelineCategory(data); toast.success("Category created!"); }
      closeCategoryModal();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save category"); }
    setSubmitting(false);
  };
  const handleEditCategory = (cat) => { setEditingCategory(cat); setCategoryForm(cat); setShowCategoryModal(true); };
  const handleDeleteCategory = async (id) => {
    if (window.confirm("Delete this category and ALL its stages?")) {
      try { await deletePipelineCategory(id); toast.success("Category deleted"); fetchPipelineStages(); }
      catch { toast.error("Failed to delete category"); }
    }
  };
  const closeCategoryModal = () => { setShowCategoryModal(false); setEditingCategory(null); setCategoryForm({ name: "", value: "", color: "#0F4C81", order: 0 }); };

  const handleOptionSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = { ...optionForm, value: optionForm.value || optionForm.label.toLowerCase().replace(/\s+/g, '_') };
      if (editingOption) { await updateDropdownOption(editingOption.id, data); toast.success("Option updated!"); }
      else { await createDropdownOption(data); toast.success("Option created!"); }
      closeOptionModal();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to save option"); }
    setSubmitting(false);
  };
  const handleEditOption = (option) => { setEditingOption(option); setOptionForm(option); setShowOptionModal(true); };
  const handleDeleteOption = async (id, category) => {
    if (window.confirm("Are you sure?")) {
      try { await deleteDropdownOption(id, category); toast.success("Option deleted"); }
      catch { toast.error("Failed to delete option"); }
    }
  };
  const closeOptionModal = () => { setShowOptionModal(false); setEditingOption(null); setOptionForm({ category: selectedCategory, value: "", label: "", color: "", order: 0, enabled: true }); };
  const openNewOptionModal = (category) => { setSelectedCategory(category); setOptionForm({ ...optionForm, category }); setShowOptionModal(true); };

  // Open invite modal and init permissions from default role
  const openInviteModal = () => {
    const defaultRole = "sales_rep";
    const defaultPerms = roles[defaultRole]?.permissions || [];
    setInviteForm({ email: "", role: defaultRole, name: "", permissions: [...defaultPerms] });
    setInviteLink("");
    setShowInviteModal(true);
  };

  return (
    <div className="min-h-screen">
      <Header title="Settings" />

      <div className="p-4 sm:p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex w-full overflow-x-auto no-scrollbar justify-start sm:w-auto sm:inline-flex">
            <TabsTrigger value="company" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-company">Company</TabsTrigger>
            <TabsTrigger value="profile" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-profile">My Profile</TabsTrigger>
            <TabsTrigger value="pipeline" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-pipeline">Pipeline</TabsTrigger>
            <TabsTrigger value="dropdowns" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-dropdowns">Dropdowns</TabsTrigger>
            <TabsTrigger value="inbox" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-inbox">Inbox</TabsTrigger>
            <TabsTrigger value="notifications" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-notifications">Notifications</TabsTrigger>
            <TabsTrigger value="imports" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-imports">Imports</TabsTrigger>
            {isAdmin && <TabsTrigger value="team" className="h-12 px-4 sm:px-6 flex-shrink-0" data-testid="tab-team">Team</TabsTrigger>}
          </TabsList>

          {/* Company Info Tab */}
          <TabsContent value="company">
            <Card className="max-w-xl">
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
                <CardDescription>This info appears on quote cover pages, invoices, and customer emails.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Name</Label>
                  <Input
                    value={companySettings.company_name}
                    onChange={e => setCompanySettings({ ...companySettings, company_name: e.target.value })}
                    placeholder="Tides Team Exteriors"
                    className="h-12"
                    data-testid="company-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={companySettings.company_phone}
                    onChange={e => setCompanySettings({ ...companySettings, company_phone: fmtPhone(e.target.value) })}
                    placeholder="843-252-4610"
                    className="h-12"
                    data-testid="company-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    value={companySettings.company_email}
                    onChange={e => setCompanySettings({ ...companySettings, company_email: e.target.value })}
                    placeholder="operations@tidesteam.com"
                    className="h-12"
                    data-testid="company-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={companySettings.company_address}
                    onChange={e => setCompanySettings({ ...companySettings, company_address: e.target.value })}
                    placeholder="201 Sigma Drive Suite 300, Summerville, SC"
                    className="h-12"
                    data-testid="company-address-input"
                  />
                </div>
                <Button
                  className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6"
                  onClick={saveCompanySettings}
                  disabled={savingCompany}
                  data-testid="save-company-btn"
                >
                  {savingCompany ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Company Info
                </Button>
              </CardContent>
            </Card>
            {isAdmin && <RebrandMigrationCard />}
          </TabsContent>

          {/* My Profile Tab — sets the direct phone that appears in
              customer emails as {{rep_phone}}. */}
          <TabsContent value="profile">
            <Card className="max-w-xl">
              <CardHeader>
                <CardTitle>My Profile</CardTitle>
                <CardDescription>
                  Your name and direct phone number. The phone here shows up in every review + referral email your customers get, so they can text or call <em>you</em> directly instead of the switchboard.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Name</Label>
                  <Input
                    value={profileForm.name}
                    onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                    className="h-12"
                    data-testid="profile-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input value={profileForm.email} disabled className="h-12 bg-slate-50 text-slate-500" data-testid="profile-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Direct Phone</Label>
                  <Input
                    value={profileForm.phone}
                    onChange={(e) => setProfileForm({ ...profileForm, phone: fmtPhone(e.target.value) })}
                    placeholder="843-555-0100"
                    className="h-12"
                    data-testid="profile-phone-input"
                  />
                  <p className="text-xs text-slate-500">Leave blank to use the company phone instead.</p>
                </div>
                <Button
                  onClick={async () => {
                    setSavingProfile(true);
                    try {
                      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/me`, {
                        name: profileForm.name,
                        phone: profileForm.phone,
                      });
                      toast.success("Profile saved");
                    } catch { toast.error("Failed to save profile"); }
                    setSavingProfile(false);
                  }}
                  disabled={savingProfile}
                  className="bg-[#0F4C81] hover:bg-[#0D4270]"
                  data-testid="profile-save-btn"
                >
                  {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Profile
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pipeline Stages Tab */}
          <TabsContent value="pipeline">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Pipeline Categories</CardTitle>
                      <CardDescription>Main pipeline tabs. Click a category to manage its stages.</CardDescription>
                    </div>
                    <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-10" onClick={() => setShowCategoryModal(true)} data-testid="add-category-btn">
                      <Plus className="w-4 h-4 mr-2" />Add Category
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {pipelineCategories.length > 0 ? (
                    <div className="space-y-2">
                      {pipelineCategories.map(cat => {
                        const catStages = pipelineStages.filter(s => s.category === cat.value);
                        const isExpanded = selectedPipelineCategory === cat.value;
                        return (
                          <div key={cat.id} data-testid={`category-item-${cat.value}`}>
                            <div
                              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors group ${isExpanded ? "bg-[#0F4C81]/5 border border-[#0F4C81]/20" : "bg-slate-50 hover:bg-slate-100"}`}
                              onClick={() => setSelectedPipelineCategory(isExpanded ? null : cat.value)}
                            >
                              <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: cat.color }} />
                              <div className="flex-1">
                                <span className="font-medium text-slate-700">{cat.name}</span>
                                <Badge variant="secondary" className="ml-2 text-xs">{catStages.length} stages</Badge>
                              </div>
                              <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditCategory(cat)}><Edit className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteCategory(cat.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </div>
                            {isExpanded && (
                              <div className="ml-7 mt-2 mb-3 space-y-1.5 border-l-2 border-slate-200 pl-4">
                                {catStages.map(stage => (
                                  <div key={stage.id} className="flex items-center gap-3 p-2.5 bg-white rounded border border-slate-100 hover:bg-slate-50 transition-colors group" data-testid={`stage-item-${stage.id}`}>
                                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color }} />
                                    <span className="flex-1 text-sm text-slate-700">{stage.name}</span>
                                    <Badge variant="outline" className="text-xs">{stage.order}</Badge>
                                    <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleEditStage(stage)}><Edit className="w-3 h-3" /></Button>
                                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteStage(stage.id)}><Trash2 className="w-3 h-3" /></Button>
                                    </div>
                                  </div>
                                ))}
                                <Button variant="outline" size="sm" className="h-8 text-xs w-full border-dashed" onClick={() => { setStageForm({ name: "", value: "", color: "#0F4C81", order: catStages.length, category: cat.value }); setShowStageModal(true); }} data-testid={`add-stage-${cat.value}-btn`}>
                                  <Plus className="w-3 h-3 mr-1" />Add Stage to {cat.name}
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-slate-500">No categories configured</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Dropdown Options Tab */}
          <TabsContent value="dropdowns">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {categories.map(cat => (
                <Card key={cat.key}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{cat.label}</CardTitle>
                      <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => openNewOptionModal(cat.key)} data-testid={`add-${cat.key}-btn`}>
                        <Plus className="w-4 h-4 mr-1" />Add
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {(dropdownOptions[cat.key] || []).length > 0 ? (
                      <div className="space-y-2">
                        {(dropdownOptions[cat.key] || []).map(option => (
                          <div key={option.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors group">
                            {option.color && <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: option.color }} />}
                            <span className="flex-1 text-sm text-slate-700">{option.label}</span>
                            <div className="flex gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handleEditOption(option)}><Edit className="w-3 h-3" /></Button>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeleteOption(option.id, cat.key)}><Trash2 className="w-3 h-3" /></Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center py-4 text-slate-500 text-sm">No options added</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <div className="space-y-6">
              <NotificationRecipientsCard />
              <NotificationRecipientsCard
                endpoint="lead-notification-recipients"
                title="Lead Alert Recipients"
                description="New-lead alerts go ONLY to these inboxes — not to the general list above. Use this when the general address is a filtered alias. Add as many as you like. Leave empty to fall back to the general list."
                allowEmpty
                testId="lead-recipients-card"
                testLabel="Send Test Lead Alert"
              />
              <PushNotificationCard />
            </div>
          </TabsContent>

          {/* Inbox Tab — SMS quick-reply template manager. */}
          <TabsContent value="inbox">
            <div className="space-y-6 max-w-3xl">
              <SmsTemplatesCard />
            </div>
          </TabsContent>

          {/* Imports Tab — bulk migration tools (Proline stalled quotes, etc.) */}
          <TabsContent value="imports">
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle>Bulk Imports</CardTitle>
                <CardDescription>Migrate legacy customer data from other CRMs.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <a
                  href="/imports/stalled-roof-quotes"
                  className="block rounded-lg border border-slate-200 p-4 hover:border-[#0F4C81] hover:bg-slate-50 transition-colors"
                  data-testid="import-link-stalled-quotes"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-900">Stalled Roof Quotes (from Proline)</p>
                      <p className="text-sm text-slate-500 mt-0.5">
                        Import unsigned quotes as Stalled Roof Quote projects. Each row starts a 22 day rescue sequence to force a decision or a clean no.
                      </p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-2" />
                  </div>
                </a>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Team Tab */}
          {isAdmin && (
            <TabsContent value="team">
              {/* Invite Section */}
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5" />Invite Team Members</CardTitle>
                      <CardDescription>Send invite links with granular permissions</CardDescription>
                    </div>
                    <Button className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={openInviteModal} data-testid="invite-member-btn">
                      <UserPlus className="w-4 h-4 mr-2" />Invite
                    </Button>
                  </div>
                </CardHeader>
                {invites.length > 0 && (
                  <CardContent>
                    <div className="space-y-2">
                      {invites.map(inv => (
                        <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-slate-50 rounded-lg" data-testid={`invite-${inv.id}`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">{inv.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs capitalize">{roles[inv.role]?.label || inv.role}</Badge>
                              <Badge className={`text-xs ${inv.status === "pending" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{inv.status}</Badge>
                              {inv.permissions && <span className="text-xs text-slate-400">{inv.permissions.length} permissions</span>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {inv.status === "pending" && (
                              <Button variant="outline" size="sm" onClick={() => copyInviteLink(inv.token)} data-testid={`copy-invite-${inv.id}`}>
                                <Copy className="w-3 h-3 mr-1" />Copy Link
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteInvite(inv.id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>

              {/* Team Members */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Shield className="w-5 h-5" />Team & Permissions</CardTitle>
                  <CardDescription>Manage individual permissions for each team member</CardDescription>
                </CardHeader>
                <CardContent>
                  {teamUsers.length > 0 ? (
                    <div className="space-y-3">
                      {teamUsers.map(member => (
                        <div key={member.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-slate-50 rounded-lg" data-testid={`team-member-${member.id}`}>
                          <div className="flex items-center gap-3 flex-1">
                            <div className="w-10 h-10 bg-[#0F4C81] rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
                              {member.name?.charAt(0) || "U"}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-medium text-slate-700 truncate">{member.name}</p>
                              <p className="text-sm text-slate-500 truncate">{member.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="capitalize text-xs">{roles[member.role]?.label || member.role}</Badge>
                            {member.role !== "admin" && (
                              <span className="text-xs text-slate-400">{(member.permissions || []).length} perms</span>
                            )}
                            {member.role !== "admin" && (
                              <Button variant="outline" size="sm" className="h-8" onClick={() => openEditMember(member)} data-testid={`edit-perms-${member.id}`}>
                                <Pencil className="w-3 h-3 mr-1" />Edit
                              </Button>
                            )}
                            {member.role === "admin" && (
                              <Badge className="bg-[#0F4C81] text-white text-xs">Full Access</Badge>
                            )}
                            {member.id !== user?.id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteMember(member)}
                                data-testid={`delete-member-${member.id}`}
                                title={member.role === "admin" ? "Remove this admin (blocked if they're the last one)" : "Remove team member"}
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500">No team members found</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={closeCategoryModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingCategory ? "Edit Category" : "Add Category"}</DialogTitle>
            <DialogDescription>Pipeline categories appear as tabs on the Pipeline page</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} placeholder="e.g., Siding" required className="h-11" data-testid="category-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <Input type="color" value={categoryForm.color} onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })} className="h-11 w-14 p-1" />
                  <Input value={categoryForm.color} onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })} className="h-11 flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input type="number" value={categoryForm.order} onChange={(e) => setCategoryForm({ ...categoryForm, order: parseInt(e.target.value) || 0 })} className="h-11" />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 h-11" onClick={closeCategoryModal}>Cancel</Button>
              <Button type="submit" className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" disabled={submitting} data-testid="submit-category-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" />Save</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stage Modal */}
      <Dialog open={showStageModal} onOpenChange={closeStageModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingStage ? "Edit Stage" : "Add Stage"}</DialogTitle>
            <DialogDescription>Configure a pipeline stage within a category</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleStageSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Stage Name *</Label>
              <Input value={stageForm.name} onChange={(e) => setStageForm({ ...stageForm, name: e.target.value })} placeholder="e.g., Inquiry Received" required className="h-11" data-testid="stage-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={stageForm.category} onValueChange={(v) => setStageForm({ ...stageForm, category: v })}>
                <SelectTrigger className="h-11"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {pipelineCategories.map(c => <SelectItem key={c.value} value={c.value}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Color</Label>
                <div className="flex gap-2">
                  <Input type="color" value={stageForm.color} onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })} className="h-11 w-14 p-1" />
                  <Input value={stageForm.color} onChange={(e) => setStageForm({ ...stageForm, color: e.target.value })} className="h-11 flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input type="number" value={stageForm.order} onChange={(e) => setStageForm({ ...stageForm, order: parseInt(e.target.value) || 0 })} className="h-11" />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 h-11" onClick={closeStageModal}>Cancel</Button>
              <Button type="submit" className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" disabled={submitting} data-testid="submit-stage-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" />Save</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Option Modal */}
      <Dialog open={showOptionModal} onOpenChange={closeOptionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingOption ? "Edit Option" : "Add Option"}</DialogTitle>
            <DialogDescription>Configure a dropdown option</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleOptionSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input value={optionForm.label} onChange={(e) => setOptionForm({ ...optionForm, label: e.target.value })} placeholder="e.g., Roofing" required className="h-12" data-testid="option-label-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Color (optional)</Label>
                <div className="flex gap-2">
                  <Input type="color" value={optionForm.color || "#6B7280"} onChange={(e) => setOptionForm({ ...optionForm, color: e.target.value })} className="h-12 w-16 p-1" />
                  <Input value={optionForm.color} onChange={(e) => setOptionForm({ ...optionForm, color: e.target.value })} placeholder="#0F4C81" className="h-12 flex-1" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input type="number" value={optionForm.order} onChange={(e) => setOptionForm({ ...optionForm, order: parseInt(e.target.value) || 0 })} className="h-12" />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 h-12" onClick={closeOptionModal}>Cancel</Button>
              <Button type="submit" className="flex-1 h-12 bg-[#0F4C81] hover:bg-[#0D4270]" disabled={submitting} data-testid="submit-option-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" />Save</>}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invite Modal with Granular Permissions */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Choose a role preset, then customize exactly what they can see and do</DialogDescription>
          </DialogHeader>
          {inviteLink ? (
            <div className="space-y-4 mt-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                <p className="text-sm font-medium text-emerald-800 mb-2">Invite Created!</p>
                <p className="text-xs text-emerald-600 mb-3">Share this link with {inviteForm.email}:</p>
                <div className="flex items-center gap-2 bg-white rounded-lg p-2 border border-emerald-200">
                  <Input value={inviteLink} readOnly className="h-9 text-xs" data-testid="invite-link-input" />
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(inviteLink); toast.success("Link copied!"); }} data-testid="copy-invite-link-btn">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <Button className="w-full" variant="outline" onClick={() => setShowInviteModal(false)}>Done</Button>
            </div>
          ) : (
            <div className="space-y-5 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm(f => ({ ...f, email: e.target.value }))} placeholder="team@example.com" className="h-11" data-testid="invite-email-input" />
                </div>
                <div className="space-y-2">
                  <Label>Name (optional)</Label>
                  <Input value={inviteForm.name} onChange={(e) => setInviteForm(f => ({ ...f, name: e.target.value }))} placeholder="Team member name" className="h-11" data-testid="invite-name-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Role Preset</Label>
                <p className="text-xs text-slate-500">Pick a starting template, then customize below</p>
                <Select value={inviteForm.role} onValueChange={handleInviteRoleChange}>
                  <SelectTrigger className="h-11" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(roles).filter(([k]) => k !== "admin").map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label} — {val.description}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Granular Permission Grid */}
              <PermissionGrid
                allPermissions={allPermissions}
                selectedPermissions={inviteForm.permissions}
                onToggle={toggleInvitePermission}
                testIdPrefix="invite"
              />

              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1 h-11" onClick={() => setShowInviteModal(false)}>Cancel</Button>
                <Button className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleCreateInvite} disabled={submitting || !inviteForm.email} data-testid="send-invite-btn">
                  {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><UserPlus className="w-5 h-5 mr-2" />Create Invite</>}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Member Permissions Modal */}
      <Dialog open={!!editingMember} onOpenChange={() => setEditingMember(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Permissions — {editingMember?.name}</DialogTitle>
            <DialogDescription>{editingMember?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label>Role</Label>
              <p className="text-xs text-slate-500">Changing the role resets permissions to that role's defaults</p>
              <Select value={editMemberRole} onValueChange={handleMemberRoleChange}>
                <SelectTrigger className="h-11" data-testid="edit-member-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(roles).filter(([k]) => k !== "admin").map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <PermissionGrid
              allPermissions={allPermissions}
              selectedPermissions={editMemberPerms}
              onToggle={toggleMemberPermission}
              testIdPrefix="edit-member"
            />

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1 h-11" onClick={() => setEditingMember(null)}>Cancel</Button>
              <Button className="flex-1 h-11 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={saveMemberPermissions} disabled={submitting} data-testid="save-member-perms-btn">
                {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" />Save Permissions</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== Permission Grid Component =====================

function PermissionGrid({ allPermissions, selectedPermissions, onToggle, testIdPrefix }) {
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (group) => {
    setExpandedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  const isGroupFullySelected = (groupPerms) => groupPerms.every(p => selectedPermissions.includes(p.key));
  const isGroupPartiallySelected = (groupPerms) => groupPerms.some(p => selectedPermissions.includes(p.key)) && !isGroupFullySelected(groupPerms);

  const toggleAllInGroup = (groupPerms) => {
    if (isGroupFullySelected(groupPerms)) {
      groupPerms.forEach(p => { if (selectedPermissions.includes(p.key)) onToggle(p.key); });
    } else {
      groupPerms.forEach(p => { if (!selectedPermissions.includes(p.key)) onToggle(p.key); });
    }
  };

  const totalSelected = selectedPermissions.length;
  const totalAvailable = Object.values(allPermissions).reduce((sum, g) => sum + g.length, 0);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-semibold">Permissions</Label>
        <span className="text-xs text-slate-500">{totalSelected} of {totalAvailable} enabled</span>
      </div>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
        {Object.entries(allPermissions).map(([group, perms]) => {
          const isExpanded = expandedGroups[group] !== false; // default open
          const allSelected = isGroupFullySelected(perms);
          const partial = isGroupPartiallySelected(perms);
          const selectedCount = perms.filter(p => selectedPermissions.includes(p.key)).length;

          return (
            <div key={group}>
              <div
                className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => toggleGroup(group)}
                data-testid={`${testIdPrefix}-group-${group.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <span className="flex-1 text-sm font-medium text-slate-700">{group}</span>
                <Badge variant={allSelected ? "default" : partial ? "secondary" : "outline"} className={`text-xs ${allSelected ? "bg-emerald-100 text-emerald-700" : ""}`}>
                  {selectedCount}/{perms.length}
                </Badge>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleAllInGroup(perms); }}
                  className={`text-xs px-2 py-1 rounded ${allSelected ? "text-red-600 hover:bg-red-50" : "text-[#0F4C81] hover:bg-blue-50"}`}
                  data-testid={`${testIdPrefix}-toggle-all-${group.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {allSelected ? "Uncheck All" : "Check All"}
                </button>
              </div>
              {isExpanded && (
                <div className="px-4 pb-3 pt-1 ml-7 space-y-2">
                  {perms.map(perm => {
                    const isOn = selectedPermissions.includes(perm.key);
                    const isWrite = perm.key.endsWith(":write");
                    return (
                      <label key={perm.key} className="flex items-center gap-3 cursor-pointer group" data-testid={`${testIdPrefix}-perm-${perm.key.replace(/:/g, "-")}`}>
                        <Switch
                          checked={isOn}
                          onCheckedChange={() => onToggle(perm.key)}
                          className="data-[state=checked]:bg-[#0F4C81]"
                        />
                        <div className="flex items-center gap-2 flex-1">
                          {isWrite ? <Pencil className="w-3.5 h-3.5 text-amber-500" /> : <Eye className="w-3.5 h-3.5 text-blue-400" />}
                          <span className="text-sm text-slate-600">{perm.label}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


// =============================================================================
// Legacy Branding Migration Card
// One-tap tool to rewrite lingering "Tides Fencing" → "Tides Team Exteriors"
// and any tidesfencing.com URLs → tidesteam.com across templates, quotes,
// invoices, workflows, and settings. Shows a dry-run preview before applying.
// =============================================================================
function RebrandMigrationCard() {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(null);
  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const runPreview = async () => {
    setLoadingPreview(true);
    setApplied(null);
    try {
      const res = await axios.get(`${API_URL}/admin/rebrand/preview`);
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to preview migration");
    } finally {
      setLoadingPreview(false);
    }
  };

  const applyMigration = async () => {
    if (!window.confirm(`Rewrite ${preview.total} document(s)? This overwrites legacy "Tides Fencing" mentions in your templates, quotes, and settings. Cannot be undone.`)) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API_URL}/admin/rebrand/apply`);
      setApplied(res.data);
      setPreview(null);
      toast.success(`${res.data.total} document(s) updated to Tides Team Exteriors branding`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed");
    } finally {
      setApplying(false);
    }
  };

  const renderReport = (report) => {
    const cols = Object.entries(report || {}).filter(([, v]) => v.length > 0);
    if (cols.length === 0) return <p className="text-sm text-slate-500 italic">Nothing to update — you&apos;re already on the new branding.</p>;
    return (
      <div className="space-y-2">
        {cols.map(([col, items]) => (
          <div key={col} className="text-xs">
            <div className="font-semibold text-slate-700 uppercase tracking-wide text-[10px]">{col} <span className="text-slate-400">({items.length})</span></div>
            <ul className="mt-1 space-y-0.5 pl-3 border-l-2 border-slate-100">
              {items.slice(0, 6).map((it, i) => (
                <li key={i} className="text-slate-600 truncate">{it}</li>
              ))}
              {items.length > 6 && <li className="text-slate-400 italic">&hellip;and {items.length - 6} more</li>}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card className="max-w-xl border-amber-200/70 bg-amber-50/30">
      <CardHeader className="pb-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-amber-700" />
          </div>
          <div className="flex-1 min-w-0">
            <CardTitle className="text-base">Rebrand Legacy Documents</CardTitle>
            <CardDescription className="text-xs mt-1 leading-relaxed">
              Rewrites lingering &quot;Tides Fencing&quot; / &quot;Tides Team Fencing&quot; mentions to
              &quot;Tides Team Exteriors&quot; and any <code className="text-[10px] bg-white/60 px-1 rounded">tidesfencing.com</code>{" "}
              URLs to <code className="text-[10px] bg-white/60 px-1 rounded">tidesteam.com</code> across your quote templates,
              quotes, invoices, workflows, and settings.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {applied ? (
          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 mb-4">
            <p className="text-sm font-semibold text-emerald-800 mb-2">
              Migration complete — {applied.total} document(s) updated.
            </p>
            {renderReport(applied.report)}
          </div>
        ) : preview ? (
          <div className="rounded-lg bg-white border border-slate-200 p-4 mb-4">
            <p className="text-sm font-semibold text-slate-800 mb-2">
              Preview — {preview.total} document(s) would change:
            </p>
            {renderReport(preview.report)}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={runPreview}
            disabled={loadingPreview || applying}
            data-testid="rebrand-preview-btn"
          >
            {loadingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
            Preview changes
          </Button>
          {preview && preview.total > 0 && (
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={applyMigration}
              disabled={applying}
              data-testid="rebrand-apply-btn"
            >
              {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              Apply to {preview.total} document(s)
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
